import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Pagination from "@/components/base/Pagination";
import MediaCard from "@/components/feature/MediaCard";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSeo } from "@/hooks/useSeo";
import {
  applyFilters,
  defaultFilterState,
  filterByType,
  sortItems,
  type FilterState,
} from "@/hooks/useMediaQuery";
import { sortOptions, typeIntroductions, typeLabels } from "@/mocks/filters";
import { type ContentType } from "@/mocks/media";
import FilterPanel from "@/pages/category/components/FilterPanel";

const PAGE_SIZE = 8;

const seoMap: Record<string, { title: string; description: string; keywords: string }> = {
  movie: {
    title: "电影 - 97视频高清影视",
    description:
      "97视频电影频道收录剧情、悬疑、科幻、动作与国风动画等类型影片，支持按类型、地区、年份多条件筛选，并按更新时间、热度与评分排序。",
    keywords: "97视频电影,高清电影,电影筛选,悬疑电影,科幻电影,电影排行榜",
  },
  tv: {
    title: "电视剧 - 97视频高清影视",
    description:
      "97视频电视剧频道整理刑侦、都市、古装、治愈等热门剧集资料，包含更新进度、集数与主演信息，支持类型、地区、年份组合筛选。",
    keywords: "97视频电视剧,热播剧集,追剧,刑侦剧,都市剧,剧集筛选",
  },
  variety: {
    title: "综艺 - 97视频高清影视",
    description:
      "97视频综艺频道汇聚访谈、真人秀、美食竞技与音乐纪实节目，支持按类型、地区、年份筛选，快速找到想看的节目。",
    keywords: "97视频综艺,综艺节目,真人秀,美食综艺,脱口秀,综艺筛选",
  },
};

export default function CategoryPage() {
  const params = useParams<{ type: string }>();
  const rawType = params.type || "movie";
  const type = (["movie", "tv", "variety"].includes(rawType) ? rawType : "movie") as ContentType;
  const [filter, setFilter] = useState<FilterState>(defaultFilterState);
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const seo = seoMap[type];
  useSeo(seo.title, seo.description, seo.keywords);

  const typeItems = useMemo(() => filterByType(type), [type]);
  const filtered = useMemo(() => applyFilters(typeItems, filter), [typeItems, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setFilter(defaultFilterState);
    setPage(1);
    setMobileFilterOpen(false);
  }, [type]);

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const resetAll = () => {
    setFilter(defaultFilterState);
    setPage(1);
  };

  const activeChips = [
    ...filter.genres.map((v) => ({ key: "genres" as const, value: v })),
    ...filter.region.map((v) => ({ key: "region" as const, value: v })),
    ...filter.year.map((v) => ({ key: "year" as const, value: v })),
  ];

  const removeChip = (key: "genres" | "region" | "year", value: string) => {
    handleFilterChange({ ...filter, [key]: filter[key].filter((v) => v !== value) });
  };

  const topRated = sortItems(typeItems, "rating").slice(0, 4);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="w-full px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line flex h-3.5 w-3.5 items-center justify-center" />
            <span className="text-foreground-800">{typeLabels[type]}</span>
          </nav>
          <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                {typeLabels[type]}频道
              </h1>
              <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-foreground-600">
                {typeIntroductions[type]}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-background-50 px-3 py-1.5 font-label text-[11px] text-foreground-600">
                共 {typeItems.length} 部
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1.5 font-label text-[11px] text-accent-800">
                高分 {typeItems.filter((i) => i.rating >= 8.5).length} 部
              </span>
              <span className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-[11px] text-secondary-800">
                {type === "movie" ? "单片" : "多集"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-7">
          <aside className="hidden w-[248px] shrink-0 lg:block">
            <div className="sticky top-[88px]">
              <FilterPanel type={type} filter={filter} onChange={handleFilterChange} resultCount={filtered.length} />
              <div className="mt-4 rounded-lg border border-background-200 bg-background-100/60 px-4 py-4">
                <h3 className="font-heading text-[13px] font-semibold text-foreground-950">口碑榜</h3>
                <ul className="mt-3 flex flex-col gap-2.5">
                  {topRated.map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/detail/${item.id}`} className="group flex items-center gap-2.5">
                        <span
                          className={`flex h-4 w-4 items-center justify-center rounded-sm font-label text-[10px] font-bold ${
                            index === 0 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="line-clamp-1-custom flex-1 text-[12px] text-foreground-700 group-hover:text-accent-600">
                          {item.title}
                        </span>
                        <span className="font-label text-[11px] font-semibold text-accent-600">
                          {item.rating.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          <section className="min-w-0 flex-1">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileFilterOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-[12px] text-foreground-800 transition-colors hover:border-accent-300 lg:hidden"
              >
                <i className="ri-filter-3-line flex h-4 w-4 items-center justify-center" />
                筛选
                {activeChips.length > 0 ? (
                  <span className="rounded-full bg-accent-500 px-1.5 text-[10px] text-background-50">
                    {activeChips.length}
                  </span>
                ) : null}
              </button>

              <div className="hidden items-center gap-1 rounded-full border border-background-300/70 bg-background-50 p-1 sm:flex">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => handleFilterChange({ ...filter, sort: option.key })}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[12px] transition-colors ${
                      filter.sort === option.key
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <span className="ml-auto font-label text-[11px] text-foreground-500">
                第 {currentPage} / {totalPages} 页 · {filtered.length} 个结果
              </span>
            </div>

            {mobileFilterOpen ? (
              <div className="animate-fade-up mb-4 lg:hidden">
                <FilterPanel type={type} filter={filter} onChange={handleFilterChange} resultCount={filtered.length} />
              </div>
            ) : null}

            {activeChips.length > 0 ? (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {activeChips.map((chip) => (
                  <button
                    key={`${chip.key}-${chip.value}`}
                    type="button"
                    onClick={() => removeChip(chip.key, chip.value)}
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-1 text-[11px] text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    {chip.value}
                    <i className="ri-close-line flex h-3 w-3 items-center justify-center" />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handleFilterChange({ ...filter, genres: [], region: [], year: [] })}
                  className="cursor-pointer whitespace-nowrap text-[11px] text-foreground-500 hover:text-accent-600"
                >
                  清除全部
                </button>
              </div>
            ) : null}

            {pageItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
                <i className="ri-search-eye-line mx-auto flex h-12 w-12 items-center justify-center text-3xl text-foreground-400" />
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">暂无符合条件的影视内容</h2>
                <p className="mt-2 text-[13px] text-foreground-600">试着减少筛选条件，或直接查看该分类的全部内容</p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-[13px] font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-restart-line flex h-4 w-4 items-center justify-center" />
                  重置筛选条件
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {pageItems.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            )}

            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}